import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import type { IProductMetafields } from "@repo/shopify/types/api/product";
import type { Product, ProductEdge, ProductVariantConnection } from "@repo/shopify/types/generated/storefront.types";

import { COLLECTION_SORT } from "@const/constant";
import { DEFAULT_TITLE } from "@const/meta";
import { URLS } from "@const/urls";

import useLanguage from "@hooks/useLanguage";

import useCollection from "@queries/useCollection";

import { useLayoutStore } from "@store/layout";

const useCollections = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [searchParams] = useSearchParams();

  // 스토어 전체를 구독하면 토스트/메뉴 같은 무관한 변경에도 리렌더 되므로 셀렉터로 좁힌다
  const setMeta = useLayoutStore((state) => state.setMeta);

  const { lang } = useLanguage();

  const handle = pathname.includes("collections") && pathname.split("/").pop();

  const sortQueries = COLLECTION_SORT.find((item) => item.name === searchParams.get("sort"));

  /** URL 쿼리스트링(category / color / size / tag) → Shopify ProductFilter 목록 */
  const filters = useMemo(
    () =>
      [...searchParams]
        .filter(([key]) => key === "category" || key === "color" || key === "size" || key === "tag")
        .map(([key, value]) => {
          const parseValue = JSON.parse(value || "[]") as string[];
          return parseValue.map((item) => {
            switch (key) {
              case "category":
                return { productType: item };
              case "tag":
                return { tag: item };
              default:
                return { variantOption: { name: key, value: item } };
            }
          });
        })
        .flat(),
    [searchParams],
  );

  /** 컬렉션 데이터 쿼리. 대상이 URL 로 정해지므로 variables 조립은 공용 훅에 맡긴다 */
  const {
    collectionData,
    collectionLoading: loading,
    fetchMoreCollection,
  } = useCollection({
    skipCollectionList: true,
    skipFilters: true,
    collection: {
      handle: handle || undefined,
      sortKey: sortQueries?.value,
      reverse: sortQueries?.reverse,
      filters,
      first: searchParams.get("view") === "seperate" ? 12 : 25,
    },
  });

  const getExpandList = (productList: ProductEdge[]) => {
    const expandList: ProductEdge[] = [];

    productList.forEach(({ node }) => {
      const colorOptions = node.options.find((item) => item.name === "Color")?.values;

      if (colorOptions) {
        colorOptions.forEach((color) => {
          const colorList = node.variants.edges.filter(({ node: variantNode }) =>
            variantNode.selectedOptions.find((option) => option.value === color),
          );

          const newList: ProductEdge = {
            node: {
              ...node,
              options: node.options.map((option) => (option.name === "Color" ? { ...option, values: [color] } : option)),
              variants: { edges: colorList } as ProductVariantConnection,
              availableForSale: colorList.some((item) => item.node.availableForSale),
            },
          } as ProductEdge;

          expandList.push(newList);
        });
      }
    });

    return expandList;
  };

  const products = useMemo(
    () =>
      collectionData?.collection?.products.edges.filter(({ node }) => (node as Product & IProductMetafields)?.hidden?.value !== "true") ??
      [],
    [collectionData],
  );

  /** 색상별로 쪼갠 목록 (view=combined 가 아닐 때 노출) */
  const expandProducts = useMemo(() => getExpandList(products), [products]);

  const hasNextPage = useMemo(() => collectionData?.collection?.products.pageInfo.hasNextPage || false, [collectionData]);

  // 없는 handle 이면 홈으로
  useEffect(() => {
    if (collectionData && !collectionData.collection) navigate(URLS.HOME);
  }, [collectionData]);

  useEffect(() => {
    setMeta({
      title: `${collectionData?.collection ? `${collectionData?.collection?.title} - ` : ""} ${DEFAULT_TITLE}`,
    });
  }, [collectionData]);

  const visibleProducts = useMemo(
    () =>
      searchParams.get("view") === "combined"
        ? products
        : expandProducts.filter(({ node }) => (lang !== "jp" && pathname.includes("archive") ? node.availableForSale : true)),
    [searchParams, products, expandProducts, pathname],
  );

  const handleFetchMore = async () => {
    await fetchMoreCollection();
  };

  return {
    collectionData,
    loading,
    products: visibleProducts,
    productCount: products.length,
    hasNextPage,
    handleFetchMore,
  };
};

export default useCollections;
